/**
 * Alert Service
 *
 * API client for alert management endpoints.
 * Handles alert rules CRUD and triggered alerts operations.
 *
 * @module services/alert.service
 */

import type {
  ApiResponse,
  AlertRule,
  TriggeredAlert,
  CreateAlertRuleRequest,
  UpdateAlertRuleRequest,
  AlertCheckResponse,
} from '@budgira/shared';
import api from './api';

/**
 * Alert Service - API client for alert endpoints
 */
export const alertService = {
  // ============================================
  // Alert Rules CRUD
  // ============================================

  /**
   * Get all alert rules for the current user
   */
  async getRules(): Promise<ApiResponse<AlertRule[]>> {
    const response = await api.get<ApiResponse<AlertRule[]>>('/alerts/rules');
    return response.data;
  },

  /**
   * Get a specific alert rule by ID
   */
  async getRule(id: string): Promise<ApiResponse<AlertRule>> {
    const response = await api.get<ApiResponse<AlertRule>>(`/alerts/rules/${id}`);
    return response.data;
  },

  /**
   * Create a new alert rule
   */
  async createRule(data: CreateAlertRuleRequest): Promise<ApiResponse<AlertRule>> {
    const response = await api.post<ApiResponse<AlertRule>>('/alerts/rules', data);
    return response.data;
  },

  /**
   * Update an existing alert rule
   */
  async updateRule(id: string, data: UpdateAlertRuleRequest): Promise<ApiResponse<AlertRule>> {
    const response = await api.put<ApiResponse<AlertRule>>(`/alerts/rules/${id}`, data);
    return response.data;
  },

  /**
   * Delete an alert rule
   */
  async deleteRule(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    const response = await api.delete<ApiResponse<{ deleted: boolean }>>(`/alerts/rules/${id}`);
    return response.data;
  },

  /**
   * Toggle alert rule active status
   */
  async toggleRuleActive(id: string, isActive: boolean): Promise<ApiResponse<AlertRule>> {
    return this.updateRule(id, { isActive });
  },

  // ============================================
  // Triggered Alerts
  // ============================================

  /**
   * Check alerts and get unread alerts
   * This evaluates all rules and triggers new alerts if conditions are met
   */
  async checkAlerts(): Promise<ApiResponse<AlertCheckResponse>> {
    const response = await api.get<ApiResponse<AlertCheckResponse>>('/alerts/check');
    return response.data;
  },

  /**
   * Get unread alert count only (lightweight)
   */
  async getUnreadCount(): Promise<ApiResponse<{ unreadCount: number }>> {
    const response = await api.get<ApiResponse<{ unreadCount: number }>>('/alerts/unread-count');
    return response.data;
  },

  /**
   * Mark a specific alert as read
   */
  async markAsRead(alertId: string): Promise<ApiResponse<TriggeredAlert>> {
    const response = await api.post<ApiResponse<TriggeredAlert>>(`/alerts/${alertId}/read`);
    return response.data;
  },

  /**
   * Mark all alerts as read
   */
  async markAllAsRead(): Promise<ApiResponse<{ markedCount: number }>> {
    const response = await api.post<ApiResponse<{ markedCount: number }>>('/alerts/read-all');
    return response.data;
  },

  /**
   * Get alert history (including read alerts)
   */
  async getHistory(limit?: number): Promise<ApiResponse<TriggeredAlert[]>> {
    const params = limit ? { limit: limit.toString() } : undefined;
    const response = await api.get<ApiResponse<TriggeredAlert[]>>('/alerts/history', { params });
    return response.data;
  },
};

export default alertService;
